import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  Save,
  FileText,
  ListOrdered,
  HelpCircle,
  Trash2,
  Layers,
  Scale,
  MapPin,
  Building2,
  Cpu,
  BookOpen,
  CheckCircle2,
  CalendarClock,
  Landmark,
  Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  identityService,
  processService,
  licenseService,
  legislationService,
  institutionService,
} from '../../services/api';
import AiGeneratingOverlay from '../../components/License/AiGeneratingOverlay';
import EntityAutocomplete from '../../components/Common/EntityAutocomplete';
import PageHeader from '../../components/Common/PageHeader';
import Can from '../../components/Common/Can';
import { getAppHomePath } from '../../constants/navigation';
import { P } from '../../constants/permissions';
import { ensureRichTextValue, isEmptyRichText, RichTextEditor, stripHtml } from '../../components/RichTextEditor';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { partyNoun } from '../../utils/tenantProfile';
import { userFacingError } from '../../i18n/userFacingError';

const STATUS_LABELS = {
  '1': 'Aguardando abertura',
  '2': 'Processo aberto',
  '3': 'Licença emitida',
  '4': 'Solicitada prorrogação',
  '5': 'Finalizado',
  '6': 'Suspensa',
  '7': 'Cassada',
};

const MAX_EXCERPTS_IN_OBS = 10;
const MAX_COND_TOTAL = 24;

function buildTrechosNormativos(excerpts) {
  if (!Array.isArray(excerpts) || !excerpts.length) return '';
  const lines = ['--- Trechos da legislação ---'];
  excerpts.slice(0, MAX_EXCERPTS_IN_OBS).forEach((h, i) => {
    const title = h.law_title || h.law_id || `Fonte ${i + 1}`;
    const sn = String(h.snippet || '')
      .trim()
      .replace(/\s+/g, ' ');
    lines.push(`[${i + 1}] ${title}\n${sn}`);
  });
  return lines.join('\n\n');
}

function buildCitationsBlock(citations) {
  if (!Array.isArray(citations) || !citations.length) return '';
  const lines = ['--- Citações estruturadas (IA → fontes) ---'];
  citations.forEach((c, i) => {
    if (typeof c === 'string') {
      lines.push(`${i + 1}. ${c}`);
      return;
    }
    const title = c.law_title || c.law_id || '';
    const point = c.point || '';
    lines.push(`${i + 1}. ${title}${point ? ` — ${point}` : ''}`);
  });
  return lines.join('\n');
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function normalizeIsoDate(v) {
  if (v == null || v === '') return '';
  const s = String(v).trim().slice(0, 10);
  return ISO_DATE_RE.test(s) ? s : '';
}

function _normAccents(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Cruza o nome sugerido pela IA com órgãos cadastrados (institution-service) para preencher orgao_licenciador_id.
 */
function matchOrgaoLicenciadorId(orgaos, suggestedName) {
  if (!suggestedName || !Array.isArray(orgaos) || orgaos.length === 0) return '';
  const raw = _normAccents(suggestedName).trim();
  if (raw.length < 2) return '';
  for (const o of orgaos) {
    const on = _normAccents(o.name);
    if (!on) continue;
    if (on === raw || on.includes(raw) || raw.includes(on)) return o.id;
  }
  const tokens = raw.split(/\s+/).filter((t) => t.length > 3);
  for (const o of orgaos) {
    const on = _normAccents(o.name);
    for (const t of tokens) {
      if (on.includes(t)) return o.id;
    }
  }
  return '';
}

function buildLicenseScheduleFromAi(cfg, orgaos = []) {
  const empty = {
    validade: '',
    data_prorrogacao: '',
    valor: '',
    processo_licenciamento: false,
    orgao_licenciador_id: '',
    ia_orgao_sugerido_nome: '',
  };
  if (!cfg || typeof cfg !== 'object') return empty;
  const val = cfg.suggested_valor;
  const pl = cfg.suggested_processo_licenciamento;
  const sugNome =
    cfg.suggested_orgao_licenciador_name != null && String(cfg.suggested_orgao_licenciador_name).trim() !== ''
      ? String(cfg.suggested_orgao_licenciador_name).trim()
      : cfg.nome_orgao_licenciador != null && String(cfg.nome_orgao_licenciador).trim() !== ''
        ? String(cfg.nome_orgao_licenciador).trim()
        : '';
  const matched = matchOrgaoLicenciadorId(orgaos, sugNome);
  return {
    validade: normalizeIsoDate(cfg.suggested_validade),
    data_prorrogacao: normalizeIsoDate(cfg.suggested_data_prorrogacao),
    valor: val != null && val !== '' && !Number.isNaN(Number(val)) ? String(val) : '',
    processo_licenciamento: pl === true,
    orgao_licenciador_id: matched,
    ia_orgao_sugerido_nome: sugNome,
  };
}

function buildObservacaoFromAi(ai) {
  const parts = [];
  if (ai.llm_answer) {
    parts.push('--- Parecer assistido (IA) ---\n' + String(ai.llm_answer).trim());
  }
  const cfg = ai.license_configuration_suggestions;
  if (cfg && typeof cfg === 'object') {
    const lines = ['--- Configuração sugerida pela IA (conferir nos trechos normativos) ---'];
    const entries = [
      ['Validade / fim da vigência (data)', cfg.suggested_validade],
      ['Prorrogação / renovação (data)', cfg.suggested_data_prorrogacao],
      ['Valor de referência (taxa/multa, se explícito)', cfg.suggested_valor],
      [
        'Processo de licenciamento em curso',
        cfg.suggested_processo_licenciamento === true
          ? 'sim'
          : cfg.suggested_processo_licenciamento === false
            ? 'não'
            : null,
      ],
      ['Periodicidade de monitoramento / relatórios', cfg.monitoring_reporting_cadence],
      ['Renovação, revisão e cadeia LP/LI/LO', cfg.renewal_and_review_notes],
      ['Relatórios ambientais, RCA, compensação', cfg.environmental_reporting_notes],
      ['Fiscalização e auditoria', cfg.inspection_audit_notes],
      ['Demais prazos e gestão', cfg.management_notes],
      ['Órgão licenciador (nome sugerido pela IA)', cfg.suggested_orgao_licenciador_name],
      ['Instrumento / categoria (LP, LI, LO, EIA…)', cfg.suggested_license_category_notes],
      ['Licença vinculada (contexto, sem UUID)', cfg.suggested_licenca_vinculada_context],
      ['Gestão ambiental (planos, PGRS…)', cfg.suggested_gestao_notes],
      ['Competência União × Estados', cfg.competence_federal_state_notes],
    ];
    entries.forEach(([label, val]) => {
      if (val != null && String(val).trim() !== '') {
        lines.push(`• ${label}: ${String(val).trim()}`);
      }
    });
    if (lines.length > 1) {
      parts.push(lines.join('\n'));
    }
  }
  if (Array.isArray(ai.proposed_defaults) && ai.proposed_defaults.length) {
    parts.push('--- Sugestões de cadastro ---');
    ai.proposed_defaults.forEach((d) => {
      const label = d.label || d.field_id || 'Campo';
      const val = d.suggested_value != null ? String(d.suggested_value) : '';
      parts.push(`• ${label}: ${val}`);
    });
  }
  const trechos = buildTrechosNormativos(ai.relevant_excerpts);
  if (trechos) parts.push(trechos);
  const cites = buildCitationsBlock(ai.llm_citations);
  if (cites) parts.push(cites);
  if (Array.isArray(ai.warnings) && ai.warnings.length) {
    parts.push('--- Avisos ---\n' + ai.warnings.map((w) => `• ${w}`).join('\n'));
  }
  if (ai.rag_index_error) {
    parts.push('--- Índice normativo ---\n' + String(ai.rag_index_error));
  }
  return parts.join('\n\n').trim();
}

function _condClean(s) {
  return String(s)
    .trim()
    .replace(/\*\*?/g, '')
    .replace(/^["'«»]+|["'»]+$/g, '')
    .trim();
}

function _condPush(out, seen, raw, minLen = 14) {
  const v = _condClean(raw);
  if (v.length < minLen || v.length > 8000) return;
  const key = v.slice(0, 160).toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  out.push(v);
}

/**
 * Extrai possíveis condicionantes do parecer LLM: listas (-, •, 1., 1), a)…),
 * rótulos em PT (Condicionante:, Obrigação:, …) e blocos após "Condicionantes:".
 */
function parseCondicionantesFromLlmAnswer(text) {
  if (!text || typeof text !== 'string') return [];
  const out = [];
  const seen = new Set();

  // Rótulos em português (mesma linha): Condicionante 1:, Obrigação:, Medida:, etc.
  const labelRe =
    /^\s*(?:Condicionante(?:\s*(?:n[º°]?\s*)?\d+)?|Obriga(?:ç|c)ão|Medida|Item|Recomenda(?:ç|c)ão|Determina(?:ç|c)ão|Exigência|Restri(?:ç|c)ão)\s*[:.)-]\s*(.+)$/i;

  // Marcadores de lista (PT/EN)
  const bulletRe =
    /^\s*(?:[-*•–—]|\d{1,3}\s*[.)]\s+|[a-z]\)\s+|[IVXLCDM]{1,8}[.)]\s+)(.+)$/i;

  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    const t = line.trim();
    if (t.length < 10) continue;

    let m = t.match(labelRe);
    if (m) {
      _condPush(out, seen, m[1]);
      continue;
    }
    m = t.match(bulletRe);
    if (m) {
      _condPush(out, seen, m[1]);
      continue;
    }
    // "1 - texto" ou "1 – texto" (travessão como separador)
    m = t.match(/^\s*\d{1,3}\s*[-–—]\s+(.+)$/);
    if (m) _condPush(out, seen, m[1]);
  }

  // Bloco após título "Condicionantes" / "Principais condicionantes" (linhas seguintes como itens)
  const blocks = text.split(/\n\s*\n+/);
  for (const block of blocks) {
    const blines = block.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (blines.length < 2) continue;
    const head = blines[0];
    if (
      !/^(?:Principais\s+)?(?:as\s+)?(?:lista\s+de\s+)?condicionantes?(?:\s+sugeridas|\s+principais)?\s*:/i.test(
        head
      )
    )
      continue;
    for (let i = 1; i < blines.length; i++) {
      const row = blines[i];
      let m = row.match(labelRe) || row.match(bulletRe);
      if (m) _condPush(out, seen, m[1]);
      else if (row.length >= 25 && !/^---/.test(row)) _condPush(out, seen, row, 25);
    }
  }

  return out;
}

function excerptFallbackCondicionantes(excerpts) {
  if (!Array.isArray(excerpts) || !excerpts.length) return [];
  const out = [];
  for (const h of excerpts.slice(0, 6)) {
    const title = String(h.law_title || h.law_id || 'Norma federal').slice(0, 120);
    if (!title || title.length < 5) continue;
    out.push(
      `Verificar e documentar no processo o atendimento às exigências aplicáveis relacionadas a «${title}», ` +
        `incluindo estudos, licenças complementares e obrigações de monitoramento exigidas pelo órgão licenciador.`
    );
    if (out.length >= 4) break;
  }
  return out;
}

/**
 * Junta condicionantes do RAG (`proposed_conditions`), tópicos extraídos do `llm_answer`
 * e, se ainda vazio, trechos curtos do corpus.
 */
function mergeCondicionantes(ai) {
  const proposed = Array.isArray(ai.proposed_conditions)
    ? ai.proposed_conditions.map((s) => String(s).trim()).filter(Boolean)
    : [];
  const seen = new Set(proposed.map((s) => s.slice(0, 160).toLowerCase()));
  const merged = [...proposed];

  for (const line of parseCondicionantesFromLlmAnswer(ai.llm_answer || '')) {
    const key = line.slice(0, 160).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(line);
    if (merged.length >= MAX_COND_TOTAL) return merged;
  }

  if (merged.length === 0) {
    const fb = excerptFallbackCondicionantes(ai.relevant_excerpts);
    for (const line of fb) {
      const key = line.slice(0, 160).toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(line);
      if (merged.length >= MAX_COND_TOTAL) break;
    }
  }

  const final = merged.length ? merged.slice(0, MAX_COND_TOTAL) : [''];
  return final;
}

/**
 * Prioriza condicionantes estruturadas da API (texto + prazo + notas) e completa com mergeCondicionantes.
 */
function mergeStructuredCondicionantes(ai) {
  const structured = Array.isArray(ai.structured_condicionantes) ? ai.structured_condicionantes : [];
  const out = [];
  const seen = new Set();

  for (const c of structured) {
    const texto = String(c.text || c.texto || c.condicionante || '')
      .trim()
      .replace(/\*\*?/g, '');
    if (texto.length < 10) continue;
    const key = texto.slice(0, 160).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    const vd = normalizeIsoDate(c.deadline_iso || c.prazo_cumprimento || c.validade);
    const info = String(c.notes || c.informacao || '')
      .trim()
      .replace(/\*\*?/g, '');
    out.push({ texto, validade: vd, informacao: info });
    if (out.length >= MAX_COND_TOTAL) {
      return out;
    }
  }

  for (const line of mergeCondicionantes(ai)) {
    const t = String(line).trim();
    if (!t) continue;
    const key = t.slice(0, 160).toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ texto: t, validade: '', informacao: '' });
    if (out.length >= MAX_COND_TOTAL) break;
  }

  return out.length ? out : [{ texto: '', validade: '', informacao: '' }];
}

function inferCondSeverity(text) {
  const t = String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (/critico|estanqueidade|vazamento|risco|proib|imediato|emergenc/.test(t)) {
    return { label: 'Crítico', className: 'bg-red-100 text-red-800' };
  }
  if (/importante|monitoramento|separadora|agua.?oleo|prazo/.test(t)) {
    return { label: 'Importante', className: 'bg-amber-100 text-amber-800' };
  }
  return { label: 'Obrigatório', className: 'bg-emerald-100 text-emerald-800' };
}

function IaFlowSteps({ step, analyzing }) {
  const current = analyzing ? 2 : step === 1 ? 1 : 3;
  const items = [
    { n: 1, title: 'Escolha de atividade', hint: 'Empreendimento, tipo e contexto' },
    { n: 2, title: 'Análise de risco IA', hint: 'Motor de condicionantes' },
    { n: 3, title: 'Revisão Semma', hint: 'Validar e emitir a licença' },
  ];
  return (
    <nav className="mb-8 mt-2" aria-label="Etapas do fluxo">
      <ol className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-0">
        {items.map((item, idx) => {
          const done = current > item.n;
          const active = current === item.n;
          return (
            <li
              key={item.n}
              className={`relative flex flex-1 items-start gap-3 px-4 py-3 ${
                active
                  ? 'rounded-xl border-2 border-primary-500 bg-primary-50/60 shadow-sm ring-1 ring-primary-200/60'
                  : done
                    ? 'rounded-xl border border-emerald-200/90 bg-emerald-50/50'
                    : 'rounded-xl border border-dashed border-gray-200 bg-gray-50/50 opacity-80'
              } ${idx === 0 ? 'sm:rounded-r-none' : 'sm:-ml-px sm:border-l-0'} ${
                idx === items.length - 1 ? 'sm:rounded-l-none' : 'sm:rounded-none'
              }`}
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  done
                    ? 'bg-emerald-600 text-white'
                    : active
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {done ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : item.n}
              </span>
              <div className="min-w-0 pt-0.5">
                <p className={`text-sm font-semibold ${active ? 'text-primary-950' : done ? 'text-emerald-950' : 'text-gray-600'}`}>
                  {item.title}
                </p>
                <p className={`mt-0.5 text-xs ${active ? 'text-primary-900/75' : done ? 'text-emerald-900/75' : 'text-gray-500'}`}>
                  {item.hint}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** API às vezes devolve prompts do assistente em inglês; exibimos PT no passo 2. */
function localizeAssistantQuestion(q) {
  if (!q || typeof q !== 'object') return String(q);
  const raw = (q.prompt || '').trim();
  if (!raw) return JSON.stringify(q);
  if (
    /provide the environmental license number|paste an excerpt of the granted conditions/i.test(raw)
  ) {
    return 'Informe o número da licença ambiental ou cole um trecho das condições concedidas no termo.';
  }
  if (/what is the federative unit \(uf\)|where the undertaking is located/i.test(raw)) {
    return 'Qual é a UF onde se localiza o empreendimento? Órgãos e regras de licenciamento variam por estado.';
  }
  if (/project\s*\/?\s*activity type|license category/i.test(raw)) {
    return 'Qual é o tipo de projeto ou atividade (ex.: indústria, loteamento, mineração)?';
  }
  return raw;
}

const LicencaAssistidaIA = () => {
  const { t } = useTranslation('licenses');
  const { tenant } = useTenant();
  const party = partyNoun(tenant);
  const [step, setStep] = useState(1);
  const [aiLoading, setAiLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [aiRaw, setAiRaw] = useState(null);

  const [basic, setBasic] = useState({
    tipo_licenca_id: '',
    processo_id: '',
    requerente_id: '',
    data_abertura_processo: new Date().toISOString().slice(0, 10),
    status: '1',
    empreendimento_id: '',
    state: '',
    municipality_name: '',
    municipality_ibge_code: '',
    project_type: '',
    summary: '',
    license_number: '',
    use_llm: true,
  });

  const [review, setReview] = useState({
    numero: '',
    observacao: '',
    licenseSchedule: {
      validade: '',
      data_prorrogacao: '',
      valor: '',
      processo_licenciamento: false,
      orgao_licenciador_id: '',
      ia_orgao_sugerido_nome: '',
    },
    condicionantes: [{ texto: '', validade: '', informacao: '' }],
  });

  const { data: tiposRaw } = useQuery(['tipos-licenca'], () =>
    licenseService.getTiposLicenca({ limit: 500 }).then((r) => r.data)
  );
  const { data: processosRaw } = useQuery(['processos'], () => processService.processoCrud.getList({ limit: 500 }));
  const { data: licencasRaw } = useQuery(['licencas', 'ia-assistida'], () =>
    licenseService.licencaCrud.getList({ limit: 500 })
  );
  const { data: clientsRaw } = useQuery(['clients'], () =>
    identityService.getClients({ limit: 500 }).then((r) => r.data)
  );
  const { data: orgaosRaw } = useQuery(['orgaos', 'licenca-ia'], () =>
    institutionService.orgaoCrud.getList({ limit: 500 })
  );
  const { data: enterprisesRaw } = useQuery(['enterprises', 'licenca-ia'], () =>
    identityService.getEnterprises({ limit: 1000 }).then((r) => r.data)
  );

  const tipos = Array.isArray(tiposRaw) ? tiposRaw : [];
  const orgaos = useMemo(
    () => (Array.isArray(orgaosRaw) ? orgaosRaw : orgaosRaw?.data ?? []),
    [orgaosRaw]
  );
  const processos = useMemo(() => {
    return Array.isArray(processosRaw) ? processosRaw : processosRaw?.data ?? [];
  }, [processosRaw]);
  const licencas = useMemo(() => {
    return Array.isArray(licencasRaw) ? licencasRaw : [];
  }, [licencasRaw]);
  const processoIdsComLicenca = useMemo(
    () => new Set(licencas.map((l) => String(l.processo_id)).filter(Boolean)),
    [licencas]
  );
  /** O seed (e regras de negócio) costuma ter 1 licença por processo — evita 400 ao salvar. */
  const processosDisponiveis = useMemo(
    () => processos.filter((p) => p?.id && !processoIdsComLicenca.has(String(p.id))),
    [processos, processoIdsComLicenca]
  );
  const clients = Array.isArray(clientsRaw?.data)
    ? clientsRaw.data
    : Array.isArray(clientsRaw)
      ? clientsRaw
      : [];
  const enterprises = Array.isArray(enterprisesRaw) ? enterprisesRaw : enterprisesRaw?.data ?? [];
  const enterpriseOptions = useMemo(
    () => enterprises.map((e) => ({ id: e.id, label: e.name || e.nome || e.id })),
    [enterprises]
  );
  const tipoOptionsIa = useMemo(
    () => tipos.map((t) => ({ id: t.id, label: t.nome || t.name || t.id })),
    [tipos]
  );
  const processoOptionsIa = useMemo(
    () =>
      processosDisponiveis.map((p) => ({
        id: p.id,
        label: p.numero
          ? `${p.numero} (${p.id?.slice(0, 8)}…)`
          : p.id
            ? `Processo ${p.id.slice(0, 8)}…`
            : '—',
      })),
    [processosDisponiveis]
  );
  const clientOptionsIa = useMemo(
    () =>
      clients.map((c) => {
        const doc = c.document || c.cpf || c.cnpj || '';
        const base = c.name || c.email || 'Cliente';
        return { id: c.id, label: doc ? `${base} · ${doc}` : base };
      }),
    [clients]
  );

  const canRunAi = useMemo(() => {
    return (
      basic.tipo_licenca_id &&
      basic.processo_id &&
      basic.requerente_id &&
      basic.state &&
      basic.state.length === 2 &&
      basic.project_type.trim()
    );
  }, [basic]);

  useEffect(() => {
    if (!basic.processo_id || !processoIdsComLicenca.size) return;
    if (!processoIdsComLicenca.has(String(basic.processo_id))) return;
    setBasic((b) => ({ ...b, processo_id: '' }));
    toast.error(t('processHasLicense'));
  }, [basic.processo_id, processoIdsComLicenca]);

  const handleGerarIa = async (e) => {
    e.preventDefault();
    if (!canRunAi) {
      toast.error(t('fillRequired'));
      return;
    }
    setAiLoading(true);
    try {
      const { data, correlationId, jobId } = await legislationService.analyzeRegistration({
        project_type: basic.project_type.trim(),
        state: basic.state.trim().toUpperCase(),
        municipality_ibge_code: basic.municipality_ibge_code.trim() || undefined,
        municipality_name: basic.municipality_name.trim() || undefined,
        summary: isEmptyRichText(basic.summary) ? undefined : stripHtml(basic.summary) || undefined,
        license_number: basic.license_number.trim() || undefined,
        use_llm: basic.use_llm,
      });
      setAiRaw(data);
      setReview({
        numero: basic.license_number.trim() || '',
        observacao: ensureRichTextValue(buildObservacaoFromAi(data) || ''),
        licenseSchedule: buildLicenseScheduleFromAi(data.license_configuration_suggestions, orgaos),
        condicionantes: mergeStructuredCondicionantes(data),
      });
      setStep(2);
      toast.success(t('suggestionsReady'));
    } catch (err) {
      toast.error(userFacingError(err, 'analysisError'));
    } finally {
      setAiLoading(false);
    }
  };

  const updateCondField = (index, field, value) => {
    setReview((r) => {
      const next = [...r.condicionantes];
      const row = { ...next[index], [field]: value };
      next[index] = row;
      return { ...r, condicionantes: next };
    });
  };

  const addCondRow = () => {
    setReview((r) => ({
      ...r,
      condicionantes: [...r.condicionantes, { texto: '', validade: '', informacao: '' }],
    }));
  };

  const removeCondRow = (index) => {
    setReview((r) => ({
      ...r,
      condicionantes: r.condicionantes.filter((_, i) => i !== index),
    }));
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      const licPayload = {
        gestao: '1',
        tipo_licenca_id: basic.tipo_licenca_id,
        processo_id: basic.processo_id,
        requerente_id: basic.requerente_id,
        data_abertura_processo: basic.data_abertura_processo,
        status: basic.status,
        numero: review.numero.trim() || null,
        empreendimento_id: basic.empreendimento_id.trim() || null,
        validade: review.licenseSchedule.validade || null,
        data_prorrogacao: review.licenseSchedule.data_prorrogacao || null,
        valor: (() => {
          const s = review.licenseSchedule.valor;
          if (s == null || String(s).trim() === '') return null;
          const n = Number.parseFloat(String(s).replace(',', '.'));
          return Number.isFinite(n) ? n : null;
        })(),
        processo_licenciamento: !!review.licenseSchedule.processo_licenciamento,
        orgao_licenciador_id: review.licenseSchedule.orgao_licenciador_id.trim() || null,
        notificado: false,
        observacao: isEmptyRichText(review.observacao) ? null : (review.observacao || '').trim(),
      };
      const created = await licenseService.licencaCrud.create(licPayload);
      const licId = created?.id;
      if (!licId) {
        throw new Error('Resposta sem id da licença');
      }
      const condRows = review.condicionantes.filter((c) => !isEmptyRichText(c.texto));
      let n = 1;
      for (const row of condRows) {
        const texto = (row.texto || '').trim();
        const vd = normalizeIsoDate(row.validade);
        const info = String(row.informacao || '').trim();
        await licenseService.createCondicionante({
          licenca_id: licId,
          numero: n,
          condicionante: texto,
          situacao: '2',
          validade: vd || undefined,
          informacao: info || undefined,
          notificado: false,
        });
        n += 1;
      }
      if (basic.processo_id) {
        const texto = [
          stripHtml(review.observacao) || 'Fundamentação da licença assistida.',
          buildTrechosNormativos(aiRaw?.excerpts),
          buildCitationsBlock(aiRaw?.citations),
        ]
          .filter(Boolean)
          .join('\n\n')
          .slice(0, 8000);
        try {
          const midias = await processService.tipoMidiaCrud.getList({ limit: 1 });
          const tipoId = (Array.isArray(midias) ? midias : midias?.items ?? midias?.data ?? [])[0]?.id;
          if (tipoId) {
            const { data: doc } = await processService.createDocumentoProcesso({
              processo_id: basic.processo_id,
              tipo_documento_id: tipoId,
              arquivo: 'FUNDAMENTACAO-RAG',
            });
            if (doc?.id) {
              await processService.createDocumentoVersion({
                documento_processo_id: doc.id,
                informacao: texto,
                arquivo: 'FUNDAMENTACAO-RAG',
              });
            }
          }
          const { data: proc } = await processService.getProcesso(basic.processo_id);
          if (proc?.orgao_origem_id && proc?.setor_origem_id && proc?.usuario_id) {
            await processService.createTramitacao({
              processo_id: basic.processo_id,
              situacao: 2,
              orgao_origem_id: proc.orgao_origem_id,
              setor_origem_id: proc.setor_origem_id,
              responsavel_origem_id: proc.usuario_id,
              orgao_destino_id: proc.orgao_origem_id,
              setor_destino_id: proc.setor_origem_id,
              responsavel_destino_id: proc.usuario_id,
              parecer: texto,
            });
          }
        } catch (persistErr) {
          toast.error(userFacingError(persistErr, 'saveError'));
        }
      }
      toast.success(
        condRows.length
          ? `Licença salva com ${condRows.length} condicionante(s).`
          : 'Licença salva.'
      );
      setStep(1);
      setAiRaw(null);
      setReview({
        numero: '',
        observacao: '',
        licenseSchedule: {
          validade: '',
          data_prorrogacao: '',
          valor: '',
          processo_licenciamento: false,
          orgao_licenciador_id: '',
          ia_orgao_sugerido_nome: '',
        },
        condicionantes: [{ texto: '', validade: '', informacao: '' }],
      });
    } catch (err) {
      toast.error(userFacingError(err, 'saveError'));
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className={`relative mx-auto p-4 sm:p-6 ${step === 2 ? 'max-w-6xl' : 'max-w-4xl'}`}>
      <AiGeneratingOverlay
        open={aiLoading}
        title="Gerando sugestões com IA"
        subtitle={
          basic.use_llm
            ? 'Consultando a legislação e redigindo o parecer. Isso pode levar alguns minutos — não feche a aba.'
            : 'Consultando a legislação federal. Isso pode levar alguns instantes — não feche a aba.'
        }
      />

      {step === 1 && (
        <>
          <PageHeader
            title="Nova licença assistida por IA"
            description="O sistema cruza o seu processo com o corpus normativo federal, sugere parecer, observações e condicionantes — você revisa tudo antes de gravar."
            breadcrumbs={[
              { label: 'Início', to: getAppHomePath() },
              { label: 'Licenças', to: '/licenses' },
              { label: 'Assistente IA' },
            ]}
          />

          <IaFlowSteps step={1} analyzing={aiLoading} />

          <div className="mb-6 overflow-hidden rounded-xl border border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-primary-50/30 p-4 shadow-sm sm:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-200/80">
                <BookOpen className="h-5 w-5 text-primary-700" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900">Base normativa</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-600">
                  As sugestões são fundamentadas em trechos da legislação federal. Com a geração de parecer
                  ativada, o texto é redigido a partir desses trechos; desativada, você recebe os trechos
                  para montar o parecer manualmente.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleGerarIa} className="space-y-6">
            <section
              className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
              aria-labelledby="ia-sec-processo-heading"
            >
              <div className="flex flex-col gap-1 border-b border-gray-100 bg-gradient-to-r from-primary-50/90 via-white to-white px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-800 ring-1 ring-primary-200/50">
                  <Scale className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 id="ia-sec-processo-heading" className="text-base font-semibold tracking-tight text-gray-900">
                    Processo e partes
                  </h2>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Vínculos obrigatórios: tipo de licença, processo ainda sem licença vinculada e requerente cadastrado.
                  </p>
                </div>
              </div>
              <div className="p-5 sm:p-6">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <EntityAutocomplete
                    id="ia-tipo-lic"
                    label="Tipo de licença"
                    required
                    value={basic.tipo_licenca_id}
                    onChange={(id) => setBasic((b) => ({ ...b, tipo_licenca_id: id }))}
                    options={tipoOptionsIa}
                    placeholder="Buscar tipo de licença…"
                  />
                  <div className="form-group">
                    <EntityAutocomplete
                      id="ia-proc"
                      label={
                        <>
                          Processo administrativo{' '}
                          <span className="font-normal text-gray-500">(sem licença ainda)</span>
                        </>
                      }
                      required
                      value={basic.processo_id}
                      onChange={(id) => setBasic((b) => ({ ...b, processo_id: id }))}
                      options={processoOptionsIa}
                      placeholder="Buscar pelo número do processo…"
                      emptyMessage={
                        processos.length > 0 && processosDisponiveis.length === 0
                          ? 'Nenhum processo disponível (todos já têm licença)'
                          : 'Nenhum resultado'
                      }
                    />
                    {processos.length > 0 && processosDisponiveis.length === 0 ? (
                      <p className="form-help text-amber-800">
                        Todos os processos deste tenant já possuem licença. Crie um processo novo no módulo de processos ou
                        use outro ambiente de teste.
                      </p>
                    ) : null}
                  </div>
                  <div className="md:col-span-2">
                    <EntityAutocomplete
                      id="ia-cliente"
                      label={party.One}
                      required
                      value={basic.requerente_id}
                      onChange={(id) => setBasic((b) => ({ ...b, requerente_id: id }))}
                      options={clientOptionsIa}
                      placeholder={`Buscar ${party.one} por nome ou documento…`}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ia-data-abertura" className="form-label form-label-required">
                      Data de abertura do processo
                    </label>
                    <input
                      id="ia-data-abertura"
                      type="date"
                      required
                      value={basic.data_abertura_processo}
                      onChange={(e) => setBasic((b) => ({ ...b, data_abertura_processo: e.target.value }))}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ia-status-basic" className="form-label">
                      Status no fluxo
                    </label>
                    <select
                      id="ia-status-basic"
                      value={basic.status}
                      onChange={(e) => setBasic((b) => ({ ...b, status: e.target.value }))}
                      className="form-select"
                    >
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </section>

            <section
              className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
              aria-labelledby="ia-sec-loc-heading"
            >
              <div className="flex flex-col gap-1 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 ring-1 ring-slate-200/80">
                  <MapPin className="h-5 w-5" aria-hidden />
                </div>
                <div>
                  <h2 id="ia-sec-loc-heading" className="text-base font-semibold tracking-tight text-gray-900">
                    Competência e localização
                  </h2>
                  <p className="mt-0.5 text-xs text-gray-500">
                    UF e município orientam a busca por normas aplicáveis e órgãos competentes.
                  </p>
                </div>
              </div>
              <div className="p-5 sm:p-6">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                  <div className="form-group">
                    <label htmlFor="ia-uf" className="form-label form-label-required">
                      UF
                    </label>
                    <input
                      id="ia-uf"
                      type="text"
                      maxLength={2}
                      required
                      value={basic.state}
                      onChange={(e) => setBasic((b) => ({ ...b, state: e.target.value.toUpperCase() }))}
                      className="form-input uppercase tracking-widest sm:max-w-[5rem]"
                      placeholder="AL"
                      autoComplete="off"
                    />
                    <p className="form-help">Duas letras, maiúsculas</p>
                  </div>
                  <div className="form-group sm:col-span-2">
                    <label htmlFor="ia-mun-nome" className="form-label">
                      Município
                    </label>
                    <input
                      id="ia-mun-nome"
                      type="text"
                      value={basic.municipality_name}
                      onChange={(e) => setBasic((b) => ({ ...b, municipality_name: e.target.value }))}
                      className="form-input"
                      placeholder="Nome oficial do município"
                    />
                  </div>
                  <div className="form-group sm:col-span-3">
                    <label htmlFor="ia-ibge" className="form-label">
                      Código IBGE do município
                    </label>
                    <input
                      id="ia-ibge"
                      type="text"
                      inputMode="numeric"
                      value={basic.municipality_ibge_code}
                      onChange={(e) => setBasic((b) => ({ ...b, municipality_ibge_code: e.target.value }))}
                      className="form-input max-w-xs font-mono text-sm"
                      placeholder="7 dígitos — ex.: 2704302"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section
              className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
              aria-labelledby="ia-sec-ctx-heading"
            >
              <div className="flex flex-col gap-1 border-b border-gray-100 bg-gradient-to-r from-emerald-50/80 via-white to-white px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200/60">
                  <Building2 className="h-5 w-5" aria-hidden />
                </div>
                <div>
                  <h2 id="ia-sec-ctx-heading" className="text-base font-semibold tracking-tight text-gray-900">
                    Empreendimento e contexto para a IA
                  </h2>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Quanto mais específico o projeto e o resumo, melhores os trechos normativos e condicionantes sugeridos.
                  </p>
                </div>
              </div>
              <div className="p-5 sm:p-6">
                <div className="grid grid-cols-1 gap-5">
                  <div className="form-group">
                    <label htmlFor="ia-projeto" className="form-label form-label-required">
                      Tipo de projeto / atividade econômica
                    </label>
                    <input
                      id="ia-projeto"
                      type="text"
                      required
                      value={basic.project_type}
                      onChange={(e) => setBasic((b) => ({ ...b, project_type: e.target.value }))}
                      className="form-input"
                      placeholder="Ex.: indústria de beneficiamento de minério, loteamento urbano, avicultura"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ia-resumo" className="form-label">
                      Resumo, trechos ou referências (opcional)
                    </label>
                    <RichTextEditor
                      value={basic.summary}
                      onChange={(v) => setBasic((b) => ({ ...b, summary: v }))}
                      minHeight="120px"
                      placeholder="Cole trechos de estudos ambientais, número de processos relacionados, condicionantes já conhecidas ou restrições do empreendimento para enriquecer a busca."
                    />
                    <p className="form-help">Recomendado para obras complexas ou quando há normas setoriais específicas.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-5 border-t border-gray-100 pt-5 md:grid-cols-2">
                    <div className="form-group">
                      <label htmlFor="ia-lic-num" className="form-label">
                        Número da licença (se já existir)
                      </label>
                      <input
                        id="ia-lic-num"
                        type="text"
                        value={basic.license_number}
                        onChange={(e) => setBasic((b) => ({ ...b, license_number: e.target.value }))}
                        className="form-input"
                        placeholder="Ex.: LP-2026-000123"
                      />
                    </div>
                    <div className="form-group">
                      <EntityAutocomplete
                        id="ia-emp-id"
                        label="Empreendimento (opcional)"
                        value={basic.empreendimento_id}
                        onChange={(id) => setBasic((b) => ({ ...b, empreendimento_id: id }))}
                        options={enterpriseOptions}
                        placeholder="Buscar empreendimento pelo nome…"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section
              className="overflow-hidden rounded-xl border border-primary-200/80 bg-gradient-to-br from-primary-50/40 via-white to-violet-50/30 shadow-sm ring-1 ring-primary-100/60"
              aria-labelledby="ia-sec-ai-heading"
            >
              <div className="border-b border-primary-100/80 bg-primary-50/50 px-5 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-primary-200/70">
                    <Cpu className="h-5 w-5 text-primary-800" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 id="ia-sec-ai-heading" className="text-base font-semibold text-gray-900">
                      Geração do parecer
                    </h2>
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      Desative para receber só os trechos da legislação (mais rápido). Ative para o sistema
                      redigir o texto do parecer a partir desses trechos.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-5 sm:p-6">
                <label
                  htmlFor="use_llm"
                  className="flex cursor-pointer items-start gap-4 rounded-lg border border-gray-200/80 bg-white p-4 shadow-sm transition-colors hover:border-primary-200 hover:bg-primary-50/20"
                >
                  <input
                    id="use_llm"
                    type="checkbox"
                    checked={basic.use_llm}
                    onChange={(e) => setBasic((b) => ({ ...b, use_llm: e.target.checked }))}
                    className="form-check mt-1 shrink-0"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">Gerar parecer textual</span>
                      {basic.use_llm ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-900">
                          <Sparkles className="h-3 w-3" aria-hidden />
                          Ativo
                        </span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                          Somente trechos
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-gray-600">
                      A geração pode levar alguns minutos. Mantenha a aba aberta até concluir.
                    </span>
                  </span>
                </label>
              </div>
            </section>

            <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-gray-50/80 px-5 py-5 shadow-inner sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <Link
                to="/licenses"
                className="inline-flex items-center justify-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
                Voltar para a lista de licenças
              </Link>
              <Can permission={P.LICENSE_ASSISTIDA}>
                <button
                  type="submit"
                  disabled={aiLoading || !canRunAi}
                  className="btn btn-primary inline-flex min-h-[44px] items-center justify-center gap-2 px-6 shadow-md"
                >
                  {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Gerar sugestões com IA
                </button>
              </Can>
            </div>
          </form>
        </>
      )}

      {step === 2 && (
        <>
          <PageHeader
            title="Revisar e salvar licença"
            description="Valide o parecer gerado, os prazos inferidos pela IA e cada condicionante antes de gravar no cadastro oficial."
            breadcrumbs={[
              { label: 'Início', to: getAppHomePath() },
              { label: 'Licenças', to: '/licenses' },
              { label: 'Assistente IA' },
            ]}
          />

          <IaFlowSteps step={2} analyzing={false} />

          {aiRaw ? (
            <div className="mb-6 rounded-xl bg-primary-900 p-5 text-white">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-300" aria-hidden />
                  <p className="text-sm font-semibold">Motor de condicionantes IA</p>
                </div>
                <span className="text-xs font-bold uppercase tracking-wide text-emerald-300">IA ativa</span>
              </div>
              <p className="mt-2 text-sm text-emerald-100">
                Análise concluída com base na legislação aplicável ao tipo de atividade informado.
              </p>
              <p className="mt-3 text-xs font-semibold text-emerald-200">
                {review.condicionantes.filter((c) => !isEmptyRichText(c.texto)).length} sugestão(ões) pronta(s) para revisão
              </p>
            </div>
          ) : null}

          <form onSubmit={handleSalvar} className="space-y-8 pb-10">
          <div className="grid gap-6 lg:grid-cols-12 lg:gap-8">
            <aside className="space-y-6 lg:col-span-4">
              <section
                className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                aria-labelledby="ia-rev-id-heading"
              >
                <div className="flex flex-col gap-1 border-b border-gray-100 bg-gradient-to-r from-primary-50/90 via-white to-white px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-800 ring-1 ring-primary-200/50">
                    <Layers className="h-5 w-5" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h2 id="ia-rev-id-heading" className="text-base font-semibold tracking-tight text-gray-900">
                      Identificação da licença
                    </h2>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Número oficial ou provisório; pode ser preenchido depois na ficha.
                    </p>
                  </div>
                </div>
                <div className="p-5 sm:p-6">
                  <label htmlFor="ia-lic-numero" className="form-label">
                    Número da licença
                  </label>
                  <input
                    id="ia-lic-numero"
                    type="text"
                    value={review.numero}
                    onChange={(e) => setReview((r) => ({ ...r, numero: e.target.value }))}
                    className="ia-review-field"
                    placeholder="Ex.: LP-2026-000123 (opcional)"
                    autoComplete="off"
                  />
                  <p className="mt-2 text-xs leading-relaxed text-gray-500">
                    Deixe em branco se ainda não houver número publicado; o cadastro aceita licença sem numeração final.
                  </p>
                </div>
              </section>

              <section
                className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                aria-labelledby="ia-rev-sched-heading"
              >
                <div className="flex flex-col gap-1 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 ring-1 ring-slate-200/80">
                    <CalendarClock className="h-5 w-5" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h2 id="ia-rev-sched-heading" className="text-base font-semibold tracking-tight text-gray-900">
                      Prazos, valor e órgão
                    </h2>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Campos frequentemente inferidos pelo modelo a partir dos trechos normativos — confira antes de salvar.
                    </p>
                  </div>
                </div>
                <div className="space-y-4 p-5 sm:p-6">
                  <div>
                    <label htmlFor="ia-lic-validade" className="mb-1.5 block text-xs font-medium text-gray-600">
                      Validade da licença
                    </label>
                    <input
                      id="ia-lic-validade"
                      type="date"
                      value={review.licenseSchedule.validade}
                      onChange={(e) =>
                        setReview((r) => ({
                          ...r,
                          licenseSchedule: { ...r.licenseSchedule, validade: e.target.value },
                        }))
                      }
                      className="ia-review-field"
                    />
                  </div>
                  <div>
                    <label htmlFor="ia-lic-prorrog" className="mb-1.5 block text-xs font-medium text-gray-600">
                      Prorrogação / renovação
                    </label>
                    <input
                      id="ia-lic-prorrog"
                      type="date"
                      value={review.licenseSchedule.data_prorrogacao}
                      onChange={(e) =>
                        setReview((r) => ({
                          ...r,
                          licenseSchedule: { ...r.licenseSchedule, data_prorrogacao: e.target.value },
                        }))
                      }
                      className="ia-review-field"
                    />
                  </div>
                  <div>
                    <label htmlFor="ia-lic-valor" className="mb-1.5 block text-xs font-medium text-gray-600">
                      Valor de referência
                    </label>
                    <input
                      id="ia-lic-valor"
                      type="text"
                      inputMode="decimal"
                      value={review.licenseSchedule.valor}
                      onChange={(e) =>
                        setReview((r) => ({
                          ...r,
                          licenseSchedule: { ...r.licenseSchedule, valor: e.target.value },
                        }))
                      }
                      className="ia-review-field"
                      placeholder="Opcional — taxas, multas, se explícito nos trechos"
                    />
                  </div>
                  <div className="border-t border-gray-100 pt-4">
                    <div className="mb-3 flex items-center gap-2 text-gray-900">
                      <Landmark className="h-4 w-4 text-primary-700" aria-hidden />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Órgão licenciador</span>
                    </div>
                    <label htmlFor="ia-lic-orgao" className="sr-only">
                      Órgão licenciador
                    </label>
                    <select
                      id="ia-lic-orgao"
                      value={review.licenseSchedule.orgao_licenciador_id}
                      onChange={(e) =>
                        setReview((r) => ({
                          ...r,
                          licenseSchedule: {
                            ...r.licenseSchedule,
                            orgao_licenciador_id: e.target.value,
                          },
                        }))
                      }
                      className="ia-review-field w-full"
                    >
                      <option value="">Nenhum / selecionar…</option>
                      {orgaos.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                    {review.licenseSchedule.ia_orgao_sugerido_nome ? (
                      <p className="mt-2 text-xs leading-relaxed text-gray-500">
                        IA sugeriu: “{review.licenseSchedule.ia_orgao_sugerido_nome}”
                        {!review.licenseSchedule.orgao_licenciador_id && orgaos.length > 0
                          ? ' — nenhum cadastro casou automaticamente; ajuste a lista se fizer sentido.'
                          : ''}
                      </p>
                    ) : null}
                  </div>
                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={review.licenseSchedule.processo_licenciamento}
                      onChange={(e) =>
                        setReview((r) => ({
                          ...r,
                          licenseSchedule: {
                            ...r.licenseSchedule,
                            processo_licenciamento: e.target.checked,
                          },
                        }))
                      }
                      className="form-check mt-0.5 shrink-0"
                    />
                    <span>Processo de licenciamento em curso</span>
                  </label>
                </div>
              </section>
            </aside>

            <div className="lg:col-span-8">
              <section
                className="overflow-hidden rounded-xl border border-primary-200/70 bg-white shadow-sm ring-1 ring-primary-100/40"
                aria-labelledby="ia-rev-parecer-heading"
              >
                <div className="border-b border-primary-100/80 bg-gradient-to-r from-primary-50/90 via-white to-violet-50/30 px-5 py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-primary-200/60">
                      <FileText className="h-5 w-5 text-primary-800" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <h2 id="ia-rev-parecer-heading" className="text-base font-semibold tracking-tight text-gray-900">
                        Parecer e fundamentação
                      </h2>
                      <p className="mt-1 text-xs leading-relaxed text-gray-600">
                        Conteúdo gravado no campo <strong className="font-medium text-gray-800">observações</strong> da
                        licença — parecer assistido, trechos do corpus federal e citações estruturadas. Edite livremente
                        antes de salvar.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-b from-slate-50/40 to-white p-5 sm:p-6">
                  <label htmlFor="ia-observacao" className="sr-only">
                    Observações e parecer
                  </label>
                  <RichTextEditor
                    value={review.observacao}
                    onChange={(v) => setReview((r) => ({ ...r, observacao: v }))}
                    minHeight="280px"
                    className="ia-review-richtext"
                    placeholder="O conteúdo sugerido pela IA aparece aqui; edite livremente."
                  />
                </div>
              </section>
            </div>
          </div>

          <section
            className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
            aria-labelledby="ia-cond-heading"
          >
            <div className="flex flex-col gap-4 border-b border-gray-100 bg-gradient-to-r from-slate-50/90 via-white to-primary-50/20 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-800 ring-1 ring-slate-200/80">
                  <ListOrdered className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h2 id="ia-cond-heading" className="text-base font-semibold tracking-tight text-gray-900">
                    Condicionantes sugeridas
                  </h2>
                  <p className="mt-1 max-w-3xl text-xs leading-relaxed text-gray-500">
                    Cada bloco vira um registro ao salvar. A IA pode trazer{' '}
                    <strong className="font-medium text-gray-700">prazo</strong> e{' '}
                    <strong className="font-medium text-gray-700">notas</strong> quando os trechos permitem; remova ou
                    edite o que não aplicar.
                  </p>
                </div>
              </div>
              <Can permission={P.LICENSE_ASSISTIDA}>
                <div className="flex flex-wrap gap-2 self-start">
                  <button
                    type="button"
                    onClick={() =>
                      setReview((r) => ({
                        ...r,
                        condicionantes: [{ texto: '', validade: '', informacao: '' }],
                      }))
                    }
                    className="btn btn-sm btn-outline"
                  >
                    Descartar tudo
                  </button>
                  <button
                    type="button"
                    onClick={addCondRow}
                    className="btn btn-sm btn-outline inline-flex shrink-0 items-center gap-1.5 border-dashed border-primary-300 text-primary-800 hover:bg-primary-50"
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                    Adicionar condicionante
                  </button>
                </div>
              </Can>
            </div>

            <div className="p-5 sm:p-6">
            <ul className="grid gap-4 sm:grid-cols-1 xl:grid-cols-2">
              {review.condicionantes.map((line, idx) => (
                <li key={idx} className="ia-review-card group relative p-4 sm:p-5">
                  <div className="flex gap-3">
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-600 to-primary-800 text-sm font-bold text-white shadow-sm ring-2 ring-white"
                      aria-hidden
                    >
                      {idx + 1}
                    </span>
                    {(() => {
                      const sev = inferCondSeverity(stripHtml(line.texto || ''));
                      return (
                        <span className={`absolute right-14 top-4 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${sev.className}`}>
                          {sev.label}
                        </span>
                      );
                    })()}
                    <div className="min-w-0 flex-1 space-y-3">
                      <label htmlFor={`ia-cond-${idx}`} className="sr-only">
                        Condicionante {idx + 1}
                      </label>
                      <RichTextEditor
                        variant="compact"
                        value={line.texto || ''}
                        onChange={(v) => updateCondField(idx, 'texto', v)}
                        placeholder="Texto da condicionante"
                      />
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <label
                            htmlFor={`ia-cond-v-${idx}`}
                            className="mb-0.5 block text-xs font-medium text-gray-600"
                          >
                            Prazo / validade
                          </label>
                          <input
                            id={`ia-cond-v-${idx}`}
                            type="date"
                            value={line.validade || ''}
                            onChange={(e) => updateCondField(idx, 'validade', e.target.value)}
                            className="ia-review-field w-full text-sm"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label
                            htmlFor={`ia-cond-i-${idx}`}
                            className="mb-0.5 block text-xs font-medium text-gray-600"
                          >
                            Informação / notas
                          </label>
                          <input
                            id={`ia-cond-i-${idx}`}
                            type="text"
                            value={line.informacao || ''}
                            onChange={(e) => updateCondField(idx, 'informacao', e.target.value)}
                            className="ia-review-field w-full text-sm"
                            placeholder="Opcional"
                          />
                        </div>
                      </div>
                    </div>
                    {review.condicionantes.length > 1 && (
                      <Can permission={P.LICENSE_ASSISTIDA}>
                        <button
                          type="button"
                          onClick={() => removeCondRow(idx)}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          title="Remover condicionante"
                          aria-label={`Remover condicionante ${idx + 1}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </Can>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            </div>
          </section>

          {aiRaw?.questions_only_if_needed?.length > 0 && (
            <section
              className="overflow-hidden rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50/90 via-white to-amber-50/50 shadow-sm ring-1 ring-amber-100/50"
              aria-labelledby="ia-questions-heading"
            >
              <div className="border-b border-amber-200/60 bg-gradient-to-r from-amber-100/50 to-amber-50/30 px-5 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-amber-200/70">
                    <HelpCircle className="h-5 w-5 text-amber-900" aria-hidden />
                  </div>
                  <div>
                    <h3 id="ia-questions-heading" className="text-base font-semibold tracking-tight text-amber-950">
                      Perguntas sugeridas pelo assistente
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-amber-900/85">
                      Itens opcionais para completar o cadastro; não bloqueiam o salvamento.
                    </p>
                  </div>
                </div>
              </div>
              <ul className="divide-y divide-amber-100/80 px-5 py-2">
                {aiRaw.questions_only_if_needed.map((q, i) => (
                  <li key={i} className="flex gap-3 py-3 text-sm text-amber-950">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-amber-200/60 text-xs font-bold text-amber-900">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{localizeAssistantQuestion(q)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <footer className="sticky bottom-0 z-10 -mx-4 flex flex-col gap-3 rounded-t-xl border border-gray-200/90 border-b-0 bg-gray-50/95 px-4 py-4 shadow-[0_-4px_24px_-8px_rgba(15,23,42,0.08)] backdrop-blur-md sm:-mx-6 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setAiRaw(null);
              }}
              className="btn btn-ghost order-2 sm:order-1"
            >
              ← Voltar para dados básicos
            </button>
            <Can permission={P.LICENSE_ASSISTIDA}>
              <button
                type="submit"
                disabled={saveLoading}
                className="btn btn-primary order-1 inline-flex items-center justify-center gap-2 shadow-md sm:order-2 sm:min-w-[240px]"
              >
                {saveLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Salvar licença e condicionantes
              </button>
            </Can>
          </footer>
        </form>
        </>
      )}
    </div>
  );
};

export default LicencaAssistidaIA;
